import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, CheckCircle, Info, XCircle, Search } from 'lucide-react'
import { format } from 'date-fns'
import { webLogService, WebLogEntry } from '@/services/webLog.service'
import { useToast } from '@/components/ui/use-toast'

interface LogEntry {
  id: string
  timestamp: string
  level: 'info' | 'warning' | 'error' | 'success'
  category: string
  message: string
  user?: string
  ipAddress?: string
}

// Map backend log type to frontend level and category
const mapLogEntry = (webLog: WebLogEntry): LogEntry => {
  let level: LogEntry['level'] = 'info'
  let category = 'System'

  // Map type to level and category
  switch (webLog.type) {
    case 'error':
      level = 'error'
      category = 'Error'
      break
    case 'login':
      level = 'success'
      category = 'Authentication'
      break
    case 'logout':
      level = 'info'
      category = 'Authentication'
      break
    case 'action':
      level = 'info'
      category = 'Action'
      break
    default:
      level = 'info'
      category = webLog.type.charAt(0).toUpperCase() + webLog.type.slice(1)
  }

  return {
    id: webLog.id,
    timestamp: webLog.log_at,
    level,
    category,
    message: webLog.log_data || `${webLog.type} event`,
    user: webLog.user_id || 'system',
    ipAddress: webLog.ip_addr
  }
}

const getLevelIcon = (level: LogEntry['level']) => {
  switch (level) {
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-600" />
    case 'info':
      return <Info className="h-4 w-4 text-blue-600" />
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-orange-600" />
    case 'error':
      return <XCircle className="h-4 w-4 text-red-600" />
  }
}

const getLevelColor = (level: LogEntry['level']) => {
  switch (level) {
    case 'success':
      return 'bg-green-100 text-green-800 border-green-300'
    case 'info':
      return 'bg-blue-100 text-blue-800 border-blue-300'
    case 'warning':
      return 'bg-orange-100 text-orange-800 border-orange-300'
    case 'error':
      return 'bg-red-100 text-red-800 border-red-300'
  }
}

export const SystemLogsPage = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const { toast } = useToast()

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true)
        const webLogs = await webLogService.list({ days: 7, page_size: 100 })
        const mappedLogs = webLogs.map(mapLogEntry)
        setLogs(mappedLogs)
      } catch (error: any) {
        console.error('Failed to fetch logs:', error)
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to fetch system logs',
          variant: 'destructive',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchLogs()
  }, [toast])

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter
    return matchesSearch && matchesLevel
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">System Logs</h1>
        <p className="text-muted-foreground">
          Monitor system activity and troubleshoot issues
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Log Filters</CardTitle>
          <CardDescription>Filter and search system logs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="level">Log Level</Label>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger id="level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Logs</CardTitle>
          <CardDescription>
            {loading ? 'Loading logs...' : `Showing ${filteredLogs.length} of ${logs.length} log entries`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading system logs...</div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">No logs found</div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="rounded-lg border p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">{getLevelIcon(log.level)}</div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge className={getLevelColor(log.level)}>
                        {log.level.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">{log.category}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(log.timestamp), 'PPp')}
                      </span>
                    </div>
                    <p className="text-sm">{log.message}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {log.user && <span>User: {log.user}</span>}
                      {log.ipAddress && <span>IP: {log.ipAddress}</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
